import { useEffect, useState } from 'react';
import { Clock, Ban, MicOff, Lock, Inbox } from 'lucide-react';
import { api } from '../api.js';
import { UserChip } from '../components/UserChip.jsx';
import { useGuild } from './GuildLayout.jsx';

function untilLabel(iso) {
  if (!iso) return { label: 'manual', soon: false };
  const ms = new Date(iso).getTime() - Date.now();
  if (ms <= 0) return { label: 'expired', soon: true };
  const sec = Math.floor(ms / 1000);
  if (sec < 3600)   return { label: `${Math.floor(sec / 60)}m`,    soon: true };
  if (sec < 86400)  return { label: `${Math.floor(sec / 3600)}h`,  soon: sec < 7200 };
  if (sec < 604800) return { label: `${Math.floor(sec / 86400)}d`, soon: false };
  return { label: `${Math.floor(sec / 604800)}w`, soon: false };
}

function untilFromEpoch(epochSec) {
  if (!epochSec) return { label: 'manual', soon: false };
  return untilLabel(new Date(epochSec * 1000).toISOString());
}

export default function Restrictions() {
  const { guildId, channels } = useGuild();
  const [data, setData] = useState(null);
  const [toast, setToast] = useState(null);
  const [busy, setBusy] = useState(null);

  useEffect(() => { reload(); }, [guildId]);
  function reload() {
    setData(null);
    api.restrictions(guildId).then(setData).catch(err => setData({ error: err.message }));
  }

  function fireToast(type, msg) { setToast({ type, msg }); setTimeout(() => setToast(null), 2500); }

  async function action(key, fn, successMsg) {
    setBusy(key);
    try { await fn(); reload(); fireToast('success', successMsg); }
    catch (err) { fireToast('error', err.message); }
    finally { setBusy(null); }
  }

  if (!data) return <div className="card"><div className="skel skel-line med" /><div className="skel" style={{ height: 80, marginTop: 12 }} /></div>;
  if (data.error) return <div className="empty">Error: {data.error}</div>;

  const { timeouts, bans, channel_mutes, channel_jails } = data;

  return (
    <>
      <h1>Restrictions</h1>
      <p className="subtitle">Everyone currently restricted in this server — server-wide and channel-scoped — in one place.</p>

      <div className="stat-grid">
        <div className="stat warn">
          <div className="label">Server timeouts</div>
          <div className="value">{timeouts.length}</div>
          <div className="delta">Discord native timeouts</div>
        </div>
        <div className="stat danger">
          <div className="label">Server bans</div>
          <div className="value">{bans.length}</div>
          <div className="delta">guild ban list</div>
        </div>
        <div className="stat warn">
          <div className="label">Channel mutes</div>
          <div className="value">{channel_mutes.length}</div>
          <div className="delta">per-channel</div>
        </div>
        <div className="stat danger">
          <div className="label">Channel jails</div>
          <div className="value">{channel_jails.length}</div>
          <div className="delta">per-channel</div>
        </div>
      </div>

      {/* Server timeouts */}
      <h3 style={{ marginTop: '2rem' }}>Server timeouts <span className="dim" style={{ fontSize: 13, fontWeight: 400 }}>(global mute via Discord)</span></h3>
      {!timeouts.length ? (
        <div className="card empty"><div className="ico"><Clock size={32} strokeWidth={1.5} /></div><p>No active timeouts.</p></div>
      ) : (
        <div className="card" style={{ padding: 0 }}>
          <table className="table">
            <thead><tr><th>User</th><th>Lifts</th><th></th></tr></thead>
            <tbody>
              {timeouts.map(t => {
                const u = untilLabel(t.until);
                return (
                  <tr key={t.user_id}>
                    <td><UserChip guildId={guildId} userId={t.user_id} size={28} /></td>
                    <td><span className={u.soon ? 'badge warn' : 'badge'}>{u.label}</span><div className="dim" style={{ fontSize: 11 }}>{new Date(t.until).toLocaleString()}</div></td>
                    <td>
                      <button className="ghost" disabled={busy === `to:${t.user_id}`} onClick={() => action(`to:${t.user_id}`, () => api.untimeout(guildId, { user_id: t.user_id, reason: 'released from restrictions page' }), 'Timeout removed')}>
                        Release
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Server bans */}
      <h3 style={{ marginTop: '2rem' }}>Server bans <span className="dim" style={{ fontSize: 13, fontWeight: 400 }}>(banned from the server entirely)</span></h3>
      {!bans.length ? (
        <div className="card empty"><div className="ico"><Ban size={32} strokeWidth={1.5} /></div><p>No active bans.</p></div>
      ) : (
        <div className="card" style={{ padding: 0 }}>
          <table className="table">
            <thead><tr><th>User</th><th>Reason</th><th></th></tr></thead>
            <tbody>
              {bans.map(b => (
                <tr key={b.user_id}>
                  <td><UserChip guildId={guildId} userId={b.user_id} size={28} /></td>
                  <td>{b.reason || <span className="dim">—</span>}</td>
                  <td>
                    <button className="ghost" disabled={busy === `un:${b.user_id}`} onClick={() => action(`un:${b.user_id}`, () => api.unban(guildId, { user_id: b.user_id, reason: 'released from restrictions page' }), 'Unbanned')}>
                      Unban
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Channel mutes */}
      <h3 style={{ marginTop: '2rem' }}>Channel mutes <span className="dim" style={{ fontSize: 13, fontWeight: 400 }}>(per-channel)</span></h3>
      <ChannelRestrictionTable rows={channel_mutes} icon={<MicOff size={32} strokeWidth={1.5} />} label="muted" emptyMsg="No active channel-mutes." channels={channels} guildId={guildId} action={action} busy={busy} />

      {/* Channel jails */}
      <h3 style={{ marginTop: '2rem' }}>Channel jails <span className="dim" style={{ fontSize: 13, fontWeight: 400 }}>(per-channel)</span></h3>
      <ChannelRestrictionTable rows={channel_jails} icon={<Lock size={32} strokeWidth={1.5} />} label="jailed" emptyMsg="No active channel-jails." channels={channels} guildId={guildId} action={action} busy={busy} />

      {toast && <div className={`toast ${toast.type}`}>{toast.msg}</div>}
    </>
  );
}

function channelLabel(c) {
  if (!c) return null;
  if (c.type === 2)  return `🔊 ${c.name}`;
  if (c.type === 13) return `🎙️ ${c.name}`;
  if (c.type === 5)  return `📣 ${c.name}`;
  return `# ${c.name}`;
}

function ChannelRestrictionTable({ rows, icon, label, emptyMsg, channels, guildId, action, busy }) {
  if (!rows.length) return <div className="card empty"><div className="ico">{icon}</div><p>{emptyMsg}</p></div>;
  return (
    <div className="card" style={{ padding: 0 }}>
      <table className="table">
        <thead><tr><th>User</th><th>Channel</th><th>Reason</th><th>Issued by</th><th>Lifts</th><th></th></tr></thead>
        <tbody>
          {rows.map(j => {
            const channel = channels.find(c => c.id === j.channel_id);
            const u = untilFromEpoch(j.expires_at);
            return (
              <tr key={j.id}>
                <td><UserChip guildId={guildId} userId={j.user_id} /></td>
                <td>{channelLabel(channel) || <span className="dim">{j.channel_id}</span>}</td>
                <td>{j.reason || <span className="dim">—</span>}</td>
                <td><UserChip guildId={guildId} userId={j.issued_by} size={16} /></td>
                <td>{j.expires_at ? <span className={u.soon ? 'badge warn' : 'badge'}>{u.label}</span> : <span className="dim">manual</span>}</td>
                <td>
                  <button className="ghost" disabled={busy === `cj:${j.id}`} onClick={() => action(`cj:${j.id}`, () => api.releaseChannelJail(guildId, j.id), 'Released')}>
                    Release
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
